import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Table, message, Spin, Select, DatePicker, Button } from 'antd';
import { CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons';
import {
    GENERATE_DASHBOARD_TABLE_EXPORT,
    GenerateDashboardTableExportResponse,
    GenerateDashboardTableExportVariables,
} from '../../../graphql/mutations/dashboard/generateDashboardTableExport';
import AsyncReportDownloadModal, {
    dashboardAsyncReportLabelI18nKeys,
    useAsyncReportExportCooldown,
} from '../../../components/asyncReportDownload';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { showError } from '../../../utils/exceptionUtils';
import { QUERY_WEEKLY_DSP_RANKING } from '../../../graphql/queries/dashboard/queryWeeklyDspRanking';
import {
    DASHBOARD_EXCLUDE_HUB_LABELS,
    useOperableHubSelectOptions,
} from '../../../hooks/useOperableHubSelectOptions';
import {
    WeeklyDspRankingItem,
    WeeklyDspRankingQueryInput,
    QueryWeeklyDspRankingResponse,
} from '../../../graphql/types/dashboard/WeeklyDspRankingTypes';
import { WarehouseBusinessType } from '../../../graphql/types/dashboard/SortingDashboardTypes';

interface TableDataRow {
    key: string;
    sortingCode?: string | null;
    dspName: string;
    currentWeekRank: number;
    rankChange?: number | null;
    currentWeekScore: string;
    currentWeekEffectiveDays: number;
    currentWeekQualifiedDays: number;
    currentWeekUndeliveredRate: string;
    lastWeekScore?: string | null;
    lastWeekEffectiveDays?: number | null;
    lastWeekQualifiedDays?: number | null;
    lastWeekUndeliveredRate?: string | null;
    rowSpan?: number; // 用于分拣仓名称列合并
}

// 格式化排名变化，返回文本和背景色
const formatRankChange = (change: number | null | undefined): { text: string; color: string } => {
    if (change === null || change === undefined) {
        return { text: '新增', color: '#ffffff' };
    }

    const text = change === 0 ? '0' : change > 0 ? `+${change}` : `${change}`;

    // 根据排名变化值设置颜色（7个色阶）
    // 注意：排名变化是正数表示排名上升（好事），负数表示排名下降（坏事）
    let backgroundColor = '#ffffff'; // 默认白色

    if (change >= 5) {
        // 上升5名及以上：深绿色
        backgroundColor = '#d4edda';
    } else if (change >= 3) {
        // 上升3-4名：中绿色
        backgroundColor = '#e2f5e9';
    } else if (change >= 1) {
        // 上升1-2名：浅绿色
        backgroundColor = '#f0faf4';
    } else if (change === 0) {
        // 无变化：白色
        backgroundColor = '#ffffff';
    } else if (change >= -2) {
        // 下降1-2名：浅橙色
        backgroundColor = '#fff3e0';
    } else if (change >= -4) {
        // 下降3-4名：中橙色
        backgroundColor = '#ffe0b2';
    } else {
        // 下降5名及以上：浅红色
        backgroundColor = '#ffccbc';
    }

    return { text, color: backgroundColor };
};

// 格式化得分，已经是字符串格式（后端已处理）
const formatScore = (score: string | null | undefined): string => {
    if (!score || score === null || score === undefined) return '-';
    return score;
};

// 计算最近的周五和上周六
const calculateDefaultWeekRange = (): [Dayjs, Dayjs] => {
    const yesterday = dayjs().subtract(1, 'day');
    
    // 找到最近的周五（包括昨天）
    let friday = yesterday;
    while (friday.day() !== 5) { // 5 = 周五
        friday = friday.subtract(1, 'day');
    }
    
    // 上周六 = 最近周五往前推6天
    const saturday = friday.subtract(6, 'day');
    
    return [saturday, friday];
};

// 验证日期：开始日期必须是周六，结束日期必须是下周五
const validateWeekDates = (startDate: Dayjs | null, endDate: Dayjs | null): string | null => {
    if (!startDate || !endDate) {
        return null; // 允许为空，后端会自动计算
    }
    
    if (startDate.day() !== 6) { // 6 = 周六
        return '开始日期必须是周六';
    }
    
    if (endDate.day() !== 5) { // 5 = 周五
        return '结束日期必须是周五';
    }
    
    // 验证结束日期是开始日期的下周五（相差6天）
    const expectedEndDate = startDate.add(6, 'day');
    if (!endDate.isSame(expectedEndDate, 'day')) {
        return '结束日期必须是开始日期的下周五（相差6天）';
    }
    
    return null;
};

const DspRankingPage: React.FC = () => {
    const { t } = useTranslation();
    const { exportLoading, runExportWithCooldown, getExportButtonLabel } = useAsyncReportExportCooldown();
    const [selectedHubId, setSelectedHubId] = useState<number>(0);
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(() => {
        const [saturday, friday] = calculateDefaultWeekRange();
        return [saturday, friday];
    });
    const [sortConfig, setSortConfig] = useState<{
        field: string;
        order: 'ascend' | 'descend' | null;
    }>({
        field: 'currentWeekRank',
        order: 'ascend',
    });
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    const [generateDashboardTableExport] = useMutation<
        GenerateDashboardTableExportResponse,
        GenerateDashboardTableExportVariables
    >(GENERATE_DASHBOARD_TABLE_EXPORT);

    const { hubOptions, loading: hubsLoading, singleAccessibleHubId } = useOperableHubSelectOptions({
        excludeLabels: [...DASHBOARD_EXCLUDE_HUB_LABELS],
        filterBusinessTypes: [WarehouseBusinessType.LAST_MILE],
    });

    useEffect(() => {
        if (singleAccessibleHubId != null) {
            setSelectedHubId(singleAccessibleHubId);
        }
    }, [singleAccessibleHubId]);

    // 格式化日期范围用于查询
    const queryDateRange = useMemo(() => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            return { startDate: null, endDate: null };
        }
        return {
            startDate: dateRange[0].format('YYYY-MM-DD'),
            endDate: dateRange[1].format('YYYY-MM-DD'),
        };
    }, [dateRange]);

    // GraphQL查询
    const { data: rankingData, loading: rankingLoading } = useQuery<QueryWeeklyDspRankingResponse>(
        QUERY_WEEKLY_DSP_RANKING,
        {
            variables: {
                input: {
                    ...queryDateRange,
                    hubId: selectedHubId,
                } as WeeklyDspRankingQueryInput,
            },
            onError: (error) => {
                console.error('查询车队排名失败:', error);
                message.error('查询车队排名失败，请稍后重试');
            },
        }
    );

    const loading = rankingLoading;

    const handleHubChange = useCallback((value: number) => {
        setSelectedHubId(value);
    }, []);

    const handleStartDateChange = useCallback((date: Dayjs | null) => {
        if (date) {
            // 确保选择的是周六
            if (date.day() !== 6) {
                message.error('开始日期必须是周六');
                return;
            }
            // 自动计算结束日期（下周五）
            const endDate = date.add(6, 'day');
            setDateRange([date, endDate]);
        } else {
            setDateRange([null, dateRange?.[1] || null]);
        }
    }, [dateRange]);

    const handleEndDateChange = useCallback((date: Dayjs | null) => {
        if (date) {
            // 确保选择的是周五
            if (date.day() !== 5) {
                message.error('结束日期必须是周五');
                return;
            }
            // 验证与开始日期的关系
            if (dateRange?.[0]) {
                const expectedEndDate = dateRange[0].add(6, 'day');
                if (!date.isSame(expectedEndDate, 'day')) {
                    message.error('结束日期必须是开始日期的下周五（相差6天）');
                    return;
                }
            }
            setDateRange([dateRange?.[0] || null, date]);
        } else {
            setDateRange([dateRange?.[0] || null, null]);
        }
    }, [dateRange]);

    // 开始日期选择器的禁用逻辑
    const disabledStartDate = useCallback((current: Dayjs) => {
        if (!current) return false;
        // 禁用今天及之后的日期
        if (current.isAfter(dayjs().subtract(1, 'day'), 'day')) {
            return true;
        }
        // 只允许选择周六
        return current.day() !== 6;
    }, []);

    // 结束日期选择器的禁用逻辑
    const disabledEndDate = useCallback((current: Dayjs) => {
        if (!current) return false;
        // 禁用今天及之后的日期
        if (current.isAfter(dayjs().subtract(1, 'day'), 'day')) {
            return true;
        }
        // 只允许选择周五
        return current.day() !== 5;
    }, []);

    // 格式化统计时间
    const statisticTimeRange = useMemo(() => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            return '';
        }
        return `${dateRange[0].format('YYYY-MM-DD')}至${dateRange[1].format('YYYY-MM-DD')}`;
    }, [dateRange]);

    // 处理排序
    const handleTableChange = useCallback((_pagination: any, _filters: any, sorter: any) => {
        if (sorter.field) {
            setSortConfig({
                field: sorter.field,
                order: sorter.order || null,
            });
        }
    }, []);

    // 自定义排序图标点击处理
    const handleSortClick = useCallback((field: string, order: 'ascend' | 'descend') => {
        setSortConfig({
            field,
            order,
        });
    }, []);

    // 渲染自定义排序标题
    const renderSortTitle = useCallback((title: string, field: string) => {
        const isActive = sortConfig.field === field;
        const isAscend = isActive && sortConfig.order === 'ascend';
        const isDescend = isActive && sortConfig.order === 'descend';

        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                <span>{title}</span>
                <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '4px' }}>
                    <CaretUpOutlined
                        style={{
                            fontSize: '12px',
                            color: isAscend ? '#1890ff' : '#bfbfbf',
                            cursor: 'pointer',
                            marginBottom: '-4px',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSortClick(field, 'ascend');
                        }}
                    />
                    <CaretDownOutlined
                        style={{
                            fontSize: '12px',
                            color: isDescend ? '#1890ff' : '#bfbfbf',
                            cursor: 'pointer',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSortClick(field, 'descend');
                        }}
                    />
                </div>
            </div>
        );
    }, [sortConfig, handleSortClick]);

    // 构建表格数据（按分拣仓分组）
    const tableData = useMemo(() => {
        if (!rankingData?.queryWeeklyDspRanking) {
            return [];
        }

        let items = [...rankingData.queryWeeklyDspRanking];

        // 应用全局排序
        if (sortConfig.order) {
            items.sort((a, b) => {
                let aValue: any;
                let bValue: any;
                
                switch (sortConfig.field) {
                    case 'currentWeekRank':
                        aValue = a.currentWeekRank;
                        bValue = b.currentWeekRank;
                        break;
                    case 'rankChange':
                        aValue = a.rankChange ?? 999;
                        bValue = b.rankChange ?? 999;
                        break;
                    case 'currentWeekScore':
                        aValue = parseFloat(a.currentWeekScore) || 0;
                        bValue = parseFloat(b.currentWeekScore) || 0;
                        break;
                    case 'lastWeekScore':
                        aValue = a.lastWeekScore ? parseFloat(a.lastWeekScore) : -1;
                        bValue = b.lastWeekScore ? parseFloat(b.lastWeekScore) : -1;
                        break;
                    default:
                        aValue = a.currentWeekRank;
                        bValue = b.currentWeekRank;
                }
                
                if (sortConfig.order === 'ascend') {
                    return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
                } else {
                    return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
                }
            });
        } else {
            // 默认按分拣仓名称和本周排名排序
            items.sort((a, b) => {
                const aCode = a.sortingCode || '';
                const bCode = b.sortingCode || '';
                if (aCode !== bCode) {
                    return aCode.localeCompare(bCode);
                }
                return a.currentWeekRank - b.currentWeekRank;
            });
        }

        // 按分拣仓分组并设置rowSpan
        const groupedBySortingCode = new Map<string, WeeklyDspRankingItem[]>();
        items.forEach(item => {
            const key = item.sortingCode || '未知';
            if (!groupedBySortingCode.has(key)) {
                groupedBySortingCode.set(key, []);
            }
            groupedBySortingCode.get(key)!.push(item);
        });

        // 转换为表格数据
        const rows: TableDataRow[] = [];
        groupedBySortingCode.forEach((groupItems, sortingCode) => {
            groupItems.forEach((item, index) => {
                rows.push({
                    key: `${sortingCode}_${item.dspName}_${index}`,
                    sortingCode: index === 0 ? sortingCode : undefined, // 只在第一行显示分拣仓名称
                    dspName: item.dspName,
                    currentWeekRank: item.currentWeekRank,
                    rankChange: item.rankChange,
                    currentWeekScore: item.currentWeekScore,
                    currentWeekEffectiveDays: item.currentWeekEffectiveDays,
                    currentWeekQualifiedDays: item.currentWeekQualifiedDays,
                    currentWeekUndeliveredRate: item.currentWeekUndeliveredRate,
                    lastWeekScore: item.lastWeekScore,
                    lastWeekEffectiveDays: item.lastWeekEffectiveDays,
                    lastWeekQualifiedDays: item.lastWeekQualifiedDays,
                    lastWeekUndeliveredRate: item.lastWeekUndeliveredRate,
                    rowSpan: index === 0 ? groupItems.length : 0, // 第一行设置rowSpan
                });
            });
        });

        return rows;
    }, [rankingData, sortConfig]);

    // 构建表格列
    const columns: ColumnsType<TableDataRow> = useMemo(() => {
        const cols: ColumnsType<TableDataRow> = [
            {
                title: '分拣仓名称',
                dataIndex: 'sortingCode',
                key: 'sortingCode',
                width: 120,
                align: 'center',
                render: (text: string, record: TableDataRow) => {
                    return {
                        children: text || '-',
                        props: {
                            rowSpan: record.rowSpan || 0,
                        },
                    };
                },
            },
            {
                title: 'DSP名称',
                dataIndex: 'dspName',
                key: 'dspName',
                width: 150,
                align: 'center',
            },
            {
                title: '排名及变化',
                children: [
                    {
                        title: renderSortTitle('本周区域内排名', 'currentWeekRank'),
                        dataIndex: 'currentWeekRank',
                        key: 'currentWeekRank',
                        width: 120,
                        align: 'center',
                    },
                    {
                        title: renderSortTitle('排名变化', 'rankChange'),
                        dataIndex: 'rankChange',
                        key: 'rankChange',
                        width: 100,
                        align: 'center',
                        render: (value: number | null | undefined) => {
                            const { text } = formatRankChange(value);
                            return <span style={{ fontWeight: value !== 0 ? 'bold' : 'normal' }}>{text}</span>;
                        },
                        onCell: (record: TableDataRow) => {
                            const { color } = formatRankChange(record.rankChange);
                            return {
                                style: {
                                    backgroundColor: color,
                                },
                            };
                        },
                    },
                ],
            },
            {
                title: '本周得分',
                children: [
                    {
                        title: renderSortTitle('最终得分', 'currentWeekScore'),
                        dataIndex: 'currentWeekScore',
                        key: 'currentWeekScore',
                        width: 100,
                        align: 'center',
                        render: (value: string) => formatScore(value),
                    },
                    {
                        title: '有效派送天数',
                        dataIndex: 'currentWeekEffectiveDays',
                        key: 'currentWeekEffectiveDays',
                        width: 120,
                        align: 'center',
                        render: (value: number) => value,
                    },
                    {
                        title: '达标天数',
                        dataIndex: 'currentWeekQualifiedDays',
                        key: 'currentWeekQualifiedDays',
                        width: 100,
                        align: 'center',
                        render: (value: number) => value,
                    },
                    {
                        title: '未妥投率',
                        dataIndex: 'currentWeekUndeliveredRate',
                        key: 'currentWeekUndeliveredRate',
                        width: 100,
                        align: 'center',
                        render: (value: string) => value,
                    },
                ],
            },
            {
                title: '上周得分',
                children: [
                    {
                        title: renderSortTitle('最终得分', 'lastWeekScore'),
                        dataIndex: 'lastWeekScore',
                        key: 'lastWeekScore',
                        width: 100,
                        align: 'center',
                        render: (value: string | null | undefined) => formatScore(value),
                    },
                    {
                        title: '有效派送天数',
                        dataIndex: 'lastWeekEffectiveDays',
                        key: 'lastWeekEffectiveDays',
                        width: 120,
                        align: 'center',
                        render: (value: number | null | undefined) => value ?? '-',
                    },
                    {
                        title: '达标天数',
                        dataIndex: 'lastWeekQualifiedDays',
                        key: 'lastWeekQualifiedDays',
                        width: 100,
                        align: 'center',
                        render: (value: number | null | undefined) => value ?? '-',
                    },
                    {
                        title: '未妥投率',
                        dataIndex: 'lastWeekUndeliveredRate',
                        key: 'lastWeekUndeliveredRate',
                        width: 100,
                        align: 'center',
                        render: (value: string | null | undefined) => value || '-',
                    },
                ],
            },
        ];

        return cols;
    }, [sortConfig]);

    const handleDownloadRanking = useCallback(async () => {
        if (!tableData || tableData.length === 0) {
            message.warning('当前条件下无车队排名数据可导出');
            return;
        }
        if (exportLoading) {
            message.warning('导出任务生成中，请稍候');
            return;
        }
        let lastSortingCode = '';
        const exportRows = tableData.map((row) => {
            const sortingCode = row.sortingCode ?? lastSortingCode;
            if (row.sortingCode != null) lastSortingCode = row.sortingCode;
            const { text: rankChangeText } = formatRankChange(row.rankChange);
            return {
                sortingCode,
                dspName: row.dspName,
                currentWeekRank: row.currentWeekRank,
                rankChangeText,
                currentWeekScore: row.currentWeekScore,
                currentWeekEffectiveDays: row.currentWeekEffectiveDays,
                currentWeekQualifiedDays: row.currentWeekQualifiedDays,
                currentWeekUndeliveredRate: row.currentWeekUndeliveredRate,
                lastWeekScore: row.lastWeekScore ?? '-',
                lastWeekEffectiveDays: row.lastWeekEffectiveDays ?? '-',
                lastWeekQualifiedDays: row.lastWeekQualifiedDays ?? '-',
                lastWeekUndeliveredRate: row.lastWeekUndeliveredRate ?? '-',
            };
        });
        const columns = [
            { title: '分拣仓名称', dataIndex: 'sortingCode' },
            { title: 'DSP名称', dataIndex: 'dspName' },
            { title: '本周区域内排名', dataIndex: 'currentWeekRank' },
            { title: '排名变化', dataIndex: 'rankChangeText' },
            { title: '本周最终得分', dataIndex: 'currentWeekScore' },
            { title: '本周有效派送天数', dataIndex: 'currentWeekEffectiveDays' },
            { title: '本周达标天数', dataIndex: 'currentWeekQualifiedDays' },
            { title: '本周未妥投率', dataIndex: 'currentWeekUndeliveredRate' },
            { title: '上周最终得分', dataIndex: 'lastWeekScore' },
            { title: '上周有效派送天数', dataIndex: 'lastWeekEffectiveDays' },
            { title: '上周达标天数', dataIndex: 'lastWeekQualifiedDays' },
            { title: '上周未妥投率', dataIndex: 'lastWeekUndeliveredRate' },
        ];
        const headers = columns.map((c) => c.title);
        const mainRows = exportRows.map((row) =>
            columns.map((c) => {
                const v = (row as Record<string, unknown>)[c.dataIndex];
                if (v === null || v === undefined) return '';
                return String(v);
            })
        );
        await runExportWithCooldown(async () => {
            try {
                const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const auditSummary = `dspRanking ${queryDateRange.startDate ?? ''}..${queryDateRange.endDate ?? ''} hubId=${selectedHubId}`;
                const response = await generateDashboardTableExport({
                    variables: {
                        input: {
                            fileNamePrefix: 'DSP_Ranking',
                            timezone: userTimezone,
                            snapshotSheetName: 'Sheet1',
                            mainSheetName: 'Sheet1',
                            snapshotRows: null,
                            mainColumnHeaders: headers,
                            mainRows,
                            auditSummary,
                        },
                    },
                });
                const fileName = response.data?.generateDashboardTableExport;
                if (fileName) {
                    setGeneratedFileName(fileName);
                    setDownloadCompleteModalVisible(true);
                } else {
                    message.error('导出失败，请稍后重试');
                }
            } catch (error: unknown) {
                showError(error);
            }
        });
    }, [tableData, exportLoading, runExportWithCooldown, generateDashboardTableExport, queryDateRange, selectedHubId]);

    return (
        <div>
            {/* 筛选项：日期范围 + 分拣仓名称 + 下载（同一行，下载在右侧） */}
            <div className="mb-4 flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-gray-800 whitespace-nowrap">日期范围</span>
                <DatePicker
                    value={dateRange?.[0] || null}
                    onChange={handleStartDateChange}
                    format="YYYY-MM-DD"
                    placeholder="开始日期（周六）"
                    disabledDate={disabledStartDate}
                    style={{ width: 150 }}
                />
                <span className="mx-2">至</span>
                <DatePicker
                    value={dateRange?.[1] || null}
                    onChange={handleEndDateChange}
                    format="YYYY-MM-DD"
                    placeholder="结束日期（下周五）"
                    disabledDate={disabledEndDate}
                    style={{ width: 150 }}
                />
                <span className="ml-4 font-semibold text-gray-800 whitespace-nowrap">分拣仓名称</span>
                <Select
                    style={{ width: 200 }}
                    value={selectedHubId}
                    onChange={handleHubChange}
                    options={hubOptions}
                    loading={hubsLoading}
                    placeholder="选择分拣仓"
                />
                </div>
                <Button
                    type="primary"
                    onClick={() => void handleDownloadRanking()}
                    loading={exportLoading}
                    disabled={exportLoading}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{getExportButtonLabel()}</span>
                </Button>
            </div>

            {/* 数据表格 */}
            <Spin spinning={loading}>
                <Table
                    columns={columns}
                    dataSource={tableData}
                    rowKey="key"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    bordered
                    size="middle"
                    onChange={handleTableChange}
                />
            </Spin>
            <AsyncReportDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.dspWeeklyRanking}
            />
        </div>
    );
};

export default DspRankingPage;
